import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Megaphone, Clock, FileText, Wallet, Info, CheckCheck, Check } from "lucide-react";
import api from "../lib/api";
import { formatDateTimeBR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Filters } from "../components/Filters";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const ICONS = { ponto: Clock, relatorio: FileText, financeiro: Wallet, aviso: Megaphone, info: Info };

const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.q) p.set("q", f.q);
    if (f.start) p.set("start", f.start);
    if (f.end) p.set("end", f.end);
    return p.toString();
};

export default function Avisos() {
    const { user } = useAuth();
    const isGestor = user?.role === "gestor";
    const [rf, setRf] = useState({ q: "", start: "", end: "" });
    const [sf, setSf] = useState({ q: "", start: "", end: "" });

    const { data: received } = useQuery({ queryKey: ["notif-history", rf], queryFn: async () => (await api.get(`/notifications?${buildParams(rf)}`)).data });
    const { data: sent = [] } = useQuery({ queryKey: ["notices", sf], enabled: isGestor, queryFn: async () => (await api.get(`/notices?${buildParams(sf)}`)).data });
    const items = received?.items || [];
    const unread = received?.unread || 0;
    const qc = useQueryClient();
    const invalidate = () => { qc.invalidateQueries({ queryKey: ["notif-history"] }); qc.invalidateQueries({ queryKey: ["notifications"] }); };
    const markOne = async (id) => { await api.post(`/notifications/${id}/read`); invalidate(); };
    const markAll = async () => { await api.post("/notifications/read-all"); invalidate(); };

    return (
        <div>
            <PageHeader overline="Comunicação" title="Central de Avisos" description="Histórico completo de notificações recebidas e avisos enviados, com filtro por palavra-chave e período." />

            <Tabs defaultValue="recebidas">
                <TabsList className="mb-6 rounded-full">
                    <TabsTrigger value="recebidas" data-testid="tab-recebidas" className="rounded-full">Recebidas</TabsTrigger>
                    {isGestor && <TabsTrigger value="enviados" data-testid="tab-enviados" className="rounded-full">Avisos enviados</TabsTrigger>}
                </TabsList>

                <TabsContent value="recebidas">
                    <Filters q={rf.q} start={rf.start} end={rf.end} onChange={setRf} placeholder="Buscar nas notificações..." />
                    {unread > 0 && (
                        <div className="mt-4 flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">{unread} não lida(s)</span>
                            <button onClick={markAll} data-testid="avisos-mark-all" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-muted"><CheckCheck className="h-4 w-4" /> Marcar todas como lidas</button>
                        </div>
                    )}
                    <div className="mt-6 space-y-3" data-testid="received-list">
                        {items.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma notificação encontrada.</p>}
                        {items.map((n) => {
                            const Icon = ICONS[n.type] || Info;
                            return (
                                <div key={n.id} className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4 fade-up" data-testid={`avisos-item-${n.id}`}>
                                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${!n.read ? "bg-accent/15 text-accent" : "bg-muted text-muted-foreground"}`}>
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold">{n.title}</p>
                                        <p className="text-sm text-muted-foreground">{n.message}</p>
                                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">{formatDateTimeBR(n.created_at)}</p>
                                    </div>
                                    {!n.read && (
                                        <button onClick={() => markOne(n.id)} data-testid={`avisos-mark-${n.id}`} className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/25">
                                            <Check className="h-3 w-3" /> Marcar lida
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </TabsContent>

                {isGestor && (
                    <TabsContent value="enviados">
                        <Filters q={sf.q} start={sf.start} end={sf.end} onChange={setSf} placeholder="Buscar nos avisos enviados..." />
                        <div className="mt-6 space-y-3" data-testid="sent-list">
                            {sent.length === 0 && <p className="text-sm text-muted-foreground">Nenhum aviso enviado.</p>}
                            {sent.map((s) => (
                                <div key={s.id} className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4 fade-up" data-testid={`sent-item-${s.id}`}>
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary/40 text-secondary-foreground">
                                        <Megaphone className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold">{s.title}</p>
                                        <p className="text-sm text-muted-foreground">{s.message}</p>
                                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                                            {formatDateTimeBR(s.created_at)} · para {s.target} · {s.sent_to} destinatário(s) · por {s.sent_by}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
